import { useFragment } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import { cn } from '@optools/components/ui/utils';
import { BTC_GAME_FRAGMENT } from '@optools/services/operator-gateway/btc/fragments';
import { BroadcastCard } from '@optools/tools/btc-tools/components/broadcasts/broadcast-card';
import { BroadcastFilters } from '@optools/tools/btc-tools/components/broadcasts/broadcast-filters';
import { broadcastsSearchSchema } from '@optools/tools/btc-tools/schemas/broadcast';
import {
  BroadcastFilter,
  BroadcastFilterKey,
  matchesBroadcastFilters,
  sortBroadcasts,
} from '@optools/tools/btc-tools/utils/broadcasts';
import { getTeamByFeedType } from '@optools/utils/pipeline';
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { omit } from 'es-toolkit';
import { RadioTower } from 'lucide-react';

export const Route = createFileRoute('/_auth/btc-tools/schedule/$gamePk/broadcasts/')({
  component: RouteComponent,
  validateSearch: zodValidator(broadcastsSearchSchema),
});

export function RouteComponent() {
  const { date, ...filters } = useSearch({ from: '/_auth/btc-tools/schedule/$gamePk/broadcasts/' });
  const { gamePk } = useParams({ from: '/_auth/btc-tools/schedule/$gamePk' });
  const navigate = useNavigate({ from: Route.fullPath });

  const handleSetFilter = (key: BroadcastFilterKey, value: BroadcastFilter[BroadcastFilterKey]) => {
    navigate({
      replace: true,
      search: (current) => ({ ...current, [key]: value }),
      to: Route.fullPath,
    });
  };

  const handleClearFilter = (keys: BroadcastFilterKey[]) => {
    navigate({
      replace: true,
      search: (current) => {
        const { date, ...currentFilters } = current;
        return { date, ...omit(currentFilters, keys) };
      },
      to: Route.fullPath,
    });
  };

  const gameFragment = useFragment({
    fragment: BTC_GAME_FRAGMENT,
    fragmentName: 'BTCGameFields',
    from: { __typename: 'Game', gamePk },
  });

  if (!gameFragment.complete) return null;

  const { data: game } = gameFragment;
  const { away, broadcasts, home } = game;

  const filteredBroadcasts = broadcasts
    .filter((broadcast) => matchesBroadcastFilters(broadcast, filters))
    .toSorted(sortBroadcasts);

  return (
    <section
      className={cn('h-[calc(100%-42px)]', broadcasts.length === 0 && 'grid grid-rows-[auto_1fr]')}>
      <p className="mb-4 text-sm">Source data for any broadcast related to the game</p>

      {broadcasts.length > 0 ? (
        <div
          className={cn(
            'h-[calc(100%-36px)]',
            filteredBroadcasts.length === 0 && 'grid grid-rows-[auto_1fr]',
          )}>
          <BroadcastFilters
            filters={filters}
            onClearFilter={handleClearFilter}
            onSetFilter={handleSetFilter}
          />

          {filteredBroadcasts.length > 0 ? (
            <ul className="grid animate-fade-in auto-rows-fr grid-cols-[repeat(auto-fill,minmax(420px,1fr))] gap-4">
              {filteredBroadcasts.map((broadcast) => {
                const team = getTeamByFeedType({ away, feedType: broadcast.feedType, home });
                return (
                  <li key={broadcast.mediaId}>
                    <BroadcastCard broadcast={broadcast} date={date} gamePk={gamePk} team={team} />
                  </li>
                );
              })}
            </ul>
          ) : (
            <div
              className="flex animate-fade-in items-center justify-center rounded border border-dashed p-4"
              data-testid="empty-broadcast-filter-results">
              <div className="flex flex-col items-center gap-2">
                <RadioTower className="size-8" />
                <h2>No Results</h2>
                <p className="text-sm text-muted-foreground">
                  No broadcasts found for the selected filters.
                </p>
                <Button
                  variant="secondary"
                  onClick={() =>
                    handleClearFilter([
                      'availableForStreaming',
                      'feedType',
                      'language',
                      'mediaType',
                    ])
                  }>
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className="flex items-center justify-center rounded border border-dashed p-4"
          data-testid="empty-broadcasts">
          <div className="flex flex-col items-center gap-2">
            <RadioTower className="size-8" />
            <h2>No Broadcasts Available</h2>
          </div>
        </div>
      )}
    </section>
  );
}
